import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { comics, categories } from "@/mocks/comics";
import ComicCard from "@/components/feature/ComicCard";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || "all");
  const [sortBy, setSortBy] = useState<"latest" | "popular" | "rating">("latest");

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const filteredComics = useMemo(() => {
    let result = activeCategory === "all"
      ? [...comics]
      : comics.filter((c) => c.category === activeCategory);

    switch (sortBy) {
      case "popular":
        return result.sort((a, b) => b.views - a.views);
      case "rating":
        return result.sort((a, b) => b.rating - a.rating);
      case "latest":
      default:
        return result.sort(
          (a, b) =>
            new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime()
        );
    }
  }, [activeCategory, sortBy]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  return (
    <main>
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-20">
        <Breadcrumb
          items={
            activeCategory !== "all"
              ? [
                  { label: "漫画分类", href: "/category/all" },
                  { label: currentCategory?.name || "" },
                ]
              : [{ label: "全部漫画" }]
          }
        />

        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">
            {activeCategory === "all" ? "全部漫画" : currentCategory?.name + "漫画"}
          </h1>
          <p className="text-sm text-foreground-500 mt-2">
            共 {filteredComics.length} 部作品
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6 flex-nowrap">
          <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-500"
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Sort Options */}
        <div className="flex items-center gap-3 mb-6">
          <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
          <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
            {[
              { key: "latest", label: "最新" },
              { key: "popular", label: "人气" },
              { key: "rating", label: "评分" },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key as typeof sortBy)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  sortBy === opt.key
                    ? "bg-secondary-500 text-background-50"
                    : "text-foreground-600 hover:text-secondary-600"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Comic Grid */}
        {filteredComics.length > 0 ? (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 pb-14"
            data-product-shop
          >
            {filteredComics.map((comic) => (
              <ComicCard key={comic.id} {...comic} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-4"></i>
            <p className="text-foreground-500">暂无相关漫画</p>
            <Link
              to="/category/all"
              className="mt-4 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部漫画
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}